import React from 'react';

import { RefreshIcon, Text } from '@lp/ds-next';
import { Card, CardContent, Stack } from '@mui/material';
import { Alert, Button, Flex, Skeleton, Typography } from 'antd';

import { ErrorText } from './ErrorText';
import { LocalCard } from './LocalCard';
import { SuccessText } from './SuccessText';
import { usePartnerStats } from '@/features/gdpr/partnerClosure/api/getPartnerStats';

export const reasonEnum = {
  UNPAID_BILLS: 0,
  PRODUCTS: 1,
  LITIGATIONS: 2,
} as const;

const texts = {
  [reasonEnum.UNPAID_BILLS]: {
    success: {
      title: () => 'Bills OK',
      subTitle: () => 'No unpaid bills',
    },
    error: {
      title: () => 'Unpaid bills',
      subTitle: () => 'Sylob shows unpaid bills',
    },
  },
  [reasonEnum.PRODUCTS]: {
    success: {
      title: ({ total }) => `0/${total} Product`,
      subTitle: () => 'Still held by organisation',
    },
    error: {
      title: ({ count, total }: { count: number; total: number }) =>
        `${count}/${Number.isNaN(total) ? 'n.a' : total} Product${count !== 1 ? 's' : ''}`,
      subTitle: () => 'Still held by organisation',
    },
  },
  [reasonEnum.LITIGATIONS]: {
    success: {
      title: () => '0 Litigation',
      subTitle: () => 'No shipment in litigation',
    },
    error: {
      title: ({ count }) => `${count} Litigation${count !== 1 ? 's' : ''}`,
      subTitle: ({ countShipments }) =>
        `${countShipments} Shipment${countShipments !== 1 ? 's' : ''} in litigation`,
    },
  },
};

// Helper function to get card props for each check type
const getCardProps = (
  checkType: keyof typeof reasonEnum,
  isError: boolean,
  data?: any
) => {
  const textConfig = texts[reasonEnum[checkType]];
  const statusKey = isError ? 'error' : 'success';

  return {
    isError,
    title: textConfig[statusKey].title(data || {}),
    subTitle: textConfig[statusKey].subTitle(data || {}),
  };
};

export const ClosureRequestWarning = () => {
  // Fetch partner stats
  const {
    data: partnerStats,
    isPending: isLoadingPartnerStats,
    isRefetching: isRefetchingPartnerStats,
    isError: isErrorPartnerStats,
    refetch: refetchPartnerStats,
  } = usePartnerStats({
    config: {
      refetchOnWindowFocus: false, // Disable refetch on window focus
    },
  });

  // Format data for cards
  const unpaidBills = (partnerStats?.unpaidBills.value as number) > 0;
  const products = {
    count: partnerStats?.inBusiness.value || 0,
    total: partnerStats?.underContract.value,
  };
  const litigations = {
    count: partnerStats?.litigation.value || 0,
  };

  const unpaidBillsError = unpaidBills;
  const productsError = products.count !== 0;
  const litigationsError = litigations.count !== 0;

  const hasError = unpaidBillsError || productsError || litigationsError;

  let children: React.ReactNode;

  if (isErrorPartnerStats) {
    children = (
      <Flex>
        <Alert
          showIcon
          style={{ width: '100%', maxWidth: '750px' }}
          message={
            <Typography.Text>
              Failed to fetch partner stats. Please{' '}
              <Typography.Link onClick={() => refetchPartnerStats()}>
                try again.
              </Typography.Link>
            </Typography.Text>
          }
          description={
            <Typography.Text>
              If the issue persists, please contact support.
            </Typography.Text>
          }
          type="error"
        />
      </Flex>
    );
  } else if (isLoadingPartnerStats || isRefetchingPartnerStats) {
    const loadingStyle = { width: 238, height: 72, borderRadius: '10px' };

    children = (
      <Stack direction="row" gap={2} flexWrap="wrap">
        <Skeleton.Node active style={loadingStyle} />
        <Skeleton.Node active style={loadingStyle} />
        <Skeleton.Node active style={loadingStyle} />
      </Stack>
    );
  } else {
    children = (
      <Stack direction="row" gap={2} flexWrap="wrap">
        {/* Unpaid bills */}
        <LocalCard {...getCardProps('UNPAID_BILLS', unpaidBillsError)} />

        {/* Products */}
        <LocalCard
          {...getCardProps('PRODUCTS', productsError, {
            count: products.count,
            total: products.total,
          })}
        />

        {/* Litigations */}
        <LocalCard
          {...getCardProps('LITIGATIONS', litigationsError, {
            count: litigations.count,
            countShipments: litigations.count,
          })}
        />
      </Stack>
    );
  }

  return (
    <Card
      data-testid="closure-request-warning-card"
      elevation={0}
      sx={{
        borderRadius: '10px',
        overflow: 'initial',
        '& #refetch-partner-stats-button': {
          opacity: 0,
          transition: 'opacity 0.2s ease-in-out',
        },
        '&:hover #refetch-partner-stats-button': {
          opacity: 1,
        },
      }}
    >
      <CardContent>
        <Flex vertical gap="1rem">
          <Flex gap="1rem" justify="space-between">
            <Text variant="titleL" component="div">
              Closure procedure
            </Text>

            <Button
              id="refetch-partner-stats-button"
              type="text"
              icon={<RefreshIcon />}
              iconPosition="end"
              onClick={() => refetchPartnerStats()}
            />
          </Flex>

          {isErrorPartnerStats ||
          isLoadingPartnerStats ||
          isRefetchingPartnerStats ? null : hasError ? (
            <ErrorText
              data={{
                [reasonEnum.UNPAID_BILLS]: unpaidBillsError,
                [reasonEnum.PRODUCTS]: productsError,
                [reasonEnum.LITIGATIONS]: litigationsError,
              }}
            />
          ) : (
            <SuccessText />
          )}
          {children}
        </Flex>
      </CardContent>
    </Card>
  );
};
